import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Checkbox,
  Typography,
} from '@mui/material';
import { GeneralTableBox, HeadTableCell, RowTableCell, LoadingSkeleton } from '../../../common/components';
import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import { buildNotificationRowKey, hasNotificationEmails } from '../utils/notificationsUtils';

const NotificationRow = ({ item, onToggleRow, isRowSelected }) => {
  const header = item.header || {};
  const coupons = item.coupons || [];
  const subscribers = Array.isArray(header.subscribers) && header.subscribers.length
    ? header.subscribers
    : [{ name: header.subscriber, email: header.subscriber_email }];

  const nextDue = header.closest_due_date;
  const earliestDue = nextDue || (coupons[0]?.third_due_date ?? coupons[0]?.regulariza1_date);
  const totalAmount =
    header.total ??
    coupons.reduce(
      (acc, coupon) => acc + (coupon.third_due_amount ?? coupon.regulariza1_amount ?? 0),
      0
    );

  const hasValidEmail = hasNotificationEmails(header);
  const rowSelected = isRowSelected(header, coupons);

  return (
    <TableRow hover selected={rowSelected}>
      <RowTableCell padding="checkbox">
        <Checkbox
          checked={rowSelected}
          onChange={(event) => {
            if (!hasValidEmail) return;
            onToggleRow(header, coupons, event.target.checked);
          }}
          color="primary"
          disabled={!hasValidEmail}
        />
      </RowTableCell>
      <RowTableCell>{header.contract_number}</RowTableCell>
      <RowTableCell>
        {subscribers.map((subscriber, index) => (
          <Box key={subscriber.uuid || index} sx={{ mb: index < subscribers.length - 1 ? 1 : 0 }}>
            <Typography fontWeight={600}>{subscriber.name || header.subscriber}</Typography>
            <Typography variant="body2" color="text.secondary">
              {subscriber.email || 'Sin email'}
            </Typography>
          </Box>
        ))}
      </RowTableCell>
      <RowTableCell>{header.plan_name || '-'}</RowTableCell>
      <RowTableCell>{formatDate(earliestDue)}</RowTableCell>
      <RowTableCell>{coupons.length}</RowTableCell>
      <RowTableCell>{formatCurrency(totalAmount)}</RowTableCell>
      <RowTableCell>{formatCurrency(header.anniversary_overdue_debt || 0)}</RowTableCell>
      <RowTableCell>
        {formatCurrency(header.anniversary_pending_amount || 0)}
        {header.anniversary_missing_agreement_date ? (
          <Typography variant="caption" color="error" display="block">
            Falta acuerdo
          </Typography>
        ) : null}
      </RowTableCell>
      <RowTableCell>{formatCurrency(header.combined_debt_amount ?? totalAmount)}</RowTableCell>
    </TableRow>
  );
};

const DebtNotificationsDataTable = ({ data, isLoading, onToggleRow, isRowFullySelected }) => {
  if (isLoading && !data.length) {
    return <LoadingSkeleton rows={6} />;
  }

  if (!data.length) {
    return (
      <GeneralTableBox>
        <Typography variant="body1" color="text.secondary">
          No se encontraron registros.
        </Typography>
      </GeneralTableBox>
    );
  }

  return (
    <GeneralTableBox>
      <Table size="small">
        <TableHead>
          <TableRow>
            <HeadTableCell padding="checkbox" />
            <HeadTableCell>Contrato</HeadTableCell>
            <HeadTableCell>Suscriptor</HeadTableCell>
            <HeadTableCell>Plan</HeadTableCell>
            <HeadTableCell>Próximo vencimiento</HeadTableCell>
            <HeadTableCell>Cupones</HeadTableCell>
            <HeadTableCell>Total estimado</HeadTableCell>
            <HeadTableCell>Aniv. mora</HeadTableCell>
            <HeadTableCell>Aniv. saldo</HeadTableCell>
            <HeadTableCell>Deuda combinada</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <NotificationRow
              key={buildNotificationRowKey(item.header)}
              item={item}
              onToggleRow={onToggleRow}
              isRowSelected={isRowFullySelected}
            />
          ))}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default DebtNotificationsDataTable;
